import { createFileRoute, Link, Outlet, redirect, useRouter, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  Home,
  Calendar,
  MessageSquare,
  Mail,
  BarChart3,
  ArrowRightLeft,
  ScrollText,
  ShieldCheck,
  Landmark,
  Radar as RadarIcon,
  Plug,
  MoreHorizontal,
  Search,
  HelpCircle,
  Settings,
  LogOut,
  KeyRound,
  X,
  PhoneCall,
  AlertTriangle,
  FileText,
  Megaphone,
  Images,
  Palette,
  CalendarDays,
  Newspaper,
  ClipboardCheck,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useRealtimeBridge } from '@/hooks/useRealtimeBridge';
import { useActivityTracker } from '@/hooks/useActivityTracker';
import { useUserActivityHeartbeat } from '@/hooks/useUserActivityHeartbeat';
import { fetchMe } from '@/lib/auth';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import { AIChatWidget } from '@/components/ai/AIChatWidget';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { BrandIcon } from '@/components/BrandLogo';
import { ChatBell } from '@/components/chat/ChatBell';
import { RealtimeBanner } from '@/components/RealtimeBanner';
import { IdleTimerProvider } from '@/components/IdleTimerProvider';
import { ThemeToggle } from '@/components/ThemeToggle';
import { GlobalSearchDialog } from '@/components/search/GlobalSearchDialog';
import { TeamChatDrawer } from '@/components/chat/TeamChatDrawer';
import { UserAppearanceProvider } from '@/components/UserAppearanceProvider';
import { OnboardingTour, isOnboardingComplete } from '@/components/onboarding/OnboardingTour';
import { useConversations } from '@/hooks/data/useChat';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useConfirm } from '@/components/ui/confirm';

export const Route = createFileRoute('/_app')({
  beforeLoad: async () => {
    const me = await fetchMe();
    if (!me) throw redirect({ to: '/login' });
    return { me };
  },
  component: AppLayout,
});

interface NavLink {
  to: string;
  label: string;
  icon: React.ReactNode;
  exact?: boolean;
}

function AppLayout() {
  const { t } = useTranslation();
  const { profile, logout } = useAuth();
  useRealtimeBridge();
  useActivityTracker();
  useUserActivityHeartbeat();
  const router = useRouter();
  const confirm = useConfirm();

  // Logout is destructive-ish (loses unsaved work, requires re-login) and the
  // profile control used to fire it on a single stray click. Always confirm.
  const confirmLogout = async () => {
    const ok = await confirm({
      title: t('auth.logout_confirm_title', 'Abmelden?'),
      body: t('auth.logout_confirm_body', 'Sie werden abgemeldet und müssen sich erneut anmelden.'),
      confirmLabel: t('auth.logout', 'Abmelden'),
      destructive: true,
    });
    if (ok) logout.mutate();
  };
  // useRouterState is reactive — re-renders on every navigation, so the
  // active-tab highlight in the sidebar / bottom nav stays in sync. Using
  // router.state.location.pathname directly is NOT reactive.
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [searchOpen, setSearchOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [onbOpen, setOnbOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile drawer on route change.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [path]);

  // …and when the viewport grows past the breakpoint where the drawer exists
  // at all. The drawer is `lg:hidden`, so on an iPad rotated from portrait to
  // landscape it disappeared visually while staying open in state — harmless
  // as long as everything it contained was hidden with it, and no longer
  // harmless now that the language panel inside it renders through a portal.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => {
      if (mq.matches) setMobileMenuOpen(false);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Auto-open onboarding tour on first login (per browser).
  useEffect(() => {
    if (!isOnboardingComplete()) {
      const t2 = setTimeout(() => setOnbOpen(true), 800);
      return () => clearTimeout(t2);
    }
  }, []);

  // Listen for explicit "open tour" requests from /help page.
  useEffect(() => {
    const onTour = () => setOnbOpen(true);
    window.addEventListener('4m:open-tour', onTour);
    return () => window.removeEventListener('4m:open-tour', onTour);
  }, []);
  const { data: conversations = [] } = useConversations();
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count ?? 0), 0);

  // ⌘K / Ctrl+K to open Global Search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // v2 (2026-05-14, tester feedback) — sidebar reduced to the 4 main entries
  // operators actually navigate around. Standalone entries for Interessenten,
  // Aufnahme/Vermarktung property buckets, the two appointment sub-routes,
  // Besichtigungsscheine and Kaufanbote are still reachable via in-page
  // segmented controls + workflow buttons; they just don't clutter the rail.
  const main: NavLink[] = [
    { to: '/dashboard', label: t('nav.dashboard', 'Dashboard'), icon: <LayoutDashboard className="h-[18px] w-[18px]" />, exact: true },
    { to: '/clients', label: t('nav.customers', 'Kunden'), icon: <Users className="h-[18px] w-[18px]" /> },
    { to: '/appointments', label: t('nav.appointments', 'Termine'), icon: <Calendar className="h-[18px] w-[18px]" />, exact: true },
    { to: '/properties', label: t('nav.properties', 'Immobilien'), icon: <Home className="h-[18px] w-[18px]" />, exact: true },
    // Private listings without an agent, collected every 30 minutes from the
    // portals that allow it. The manual Recherche page is hidden since
    // 2026-09-13 — see routes/_app/recherche.tsx.
    { to: '/radar', label: t('nav.radar', 'Radar'), icon: <RadarIcon className="h-[18px] w-[18px]" /> },
  ];

  const comm: NavLink[] = [
    { to: '/communication', label: t('nav.communication', 'Kommunikation'), icon: <MessageSquare className="h-[18px] w-[18px]" /> },
    { to: '/email-marketing', label: t('nav.email_marketing', 'E-Mail-Marketing'), icon: <Mail className="h-[18px] w-[18px]" /> },
    { to: '/marketing-uebersicht', label: t('nav.muebersicht', 'Marketing-Übersicht'), icon: <LayoutDashboard className="h-[18px] w-[18px]" /> },
    { to: '/marketing', label: t('nav.marketing', 'Marketing'), icon: <Megaphone className="h-[18px] w-[18px]" /> },
    { to: '/mediathek', label: t('nav.mediathek', 'Mediathek'), icon: <Images className="h-[18px] w-[18px]" /> },
    { to: '/design', label: t('nav.design', 'Design'), icon: <Palette className="h-[18px] w-[18px]" /> },
    { to: '/kalender', label: t('nav.kalender', 'Kalender'), icon: <CalendarDays className="h-[18px] w-[18px]" /> },
    { to: '/blog', label: t('nav.blog', 'Blog'), icon: <Newspaper className="h-[18px] w-[18px]" /> },
    { to: '/analytics', label: t('nav.analytics', 'Analytics'), icon: <BarChart3 className="h-[18px] w-[18px]" /> },
  ];

  // /voice-calls is gated to admin1 only (operator decision —
  // testadmin1 + future admins do not see Sophie's cold-call pool).
  // agent_id is unique + stable, so it's the right anchor here.
  const isVoiceOperator = profile?.role === 'admin' && profile?.agent_id === 'admin1';
  // Batch AC — Zinshausliste visible to admin (always) + any user
  // with the per-user profiles.zinshaus_access flag flipped on.
  const hasZinshausAccess = profile?.role === 'admin' || profile?.zinshaus_access === true;
  const isAdmin = profile?.role === 'admin';
  // Seller-rules batch — Verkäufer-Aktivität compliance page: visible to
  // compliance managers (profiles.seller_compliance) + the super admin.
  // Same predicate lives in the route's beforeLoad.
  const isSellerComplianceAdmin =
    isAdmin && (profile?.seller_compliance === true || profile?.agent_id === 'admin1');
  const admin: NavLink[] = [
    ...(isAdmin && isVoiceOperator
      ? [
          {
            to: '/voice-calls',
            label: t('nav.voice_calls', 'Virtuelle Sekretärin'),
            icon: <PhoneCall className="h-[18px] w-[18px]" />,
          },
        ]
      : []),
    ...(isAdmin
      ? [
          { to: '/admin/appointments-orphans', label: t('nav.appointments_orphans', 'Termine-Zuweisung'), icon: <AlertTriangle className="h-[18px] w-[18px]" /> },
          { to: '/admin/transfers', label: t('nav.transfers', 'Transfers'), icon: <ArrowRightLeft className="h-[18px] w-[18px]" /> },
        ]
      : []),
    // Seller-rules batch — compliance managers + super admin only.
    ...(isSellerComplianceAdmin
      ? [
          {
            to: '/admin/seller-activity',
            label: t('nav.seller_activity', 'Verkäufer-Aktivität'),
            icon: <ClipboardCheck className="h-[18px] w-[18px]" />,
          },
        ]
      : []),
    // Zinshausliste — admin always, plus per-user access. Placed
    // ABOVE Audit-Log per operator decision.
    ...(hasZinshausAccess
      ? [
          {
            to: '/admin/zinshaeuser',
            label: t('nav.zinshaeuser', 'Zinshausliste'),
            icon: <Landmark className="h-[18px] w-[18px]" />,
          },
        ]
      : []),
    // Justimmo-Anbindung — reine Statusseite (liest, löst nichts aus).
    // Nur Administration, dieselbe Bedingung wie im beforeLoad der Route.
    ...(isAdmin
      ? [
          {
            to: '/admin/justimmo',
            label: t('nav.justimmo', 'Justimmo-Anbindung'),
            icon: <Plug className="h-[18px] w-[18px]" />,
          },
        ]
      : []),
    ...(isAdmin
      ? [
          { to: '/marketing-admin', label: t('nav.madmin', 'Marketing-Admin'), icon: <BarChart3 className="h-[18px] w-[18px]" /> },
          { to: '/admin/audit-log', label: t('nav.audit', 'Audit-Log'), icon: <ScrollText className="h-[18px] w-[18px]" /> },
          { to: '/admin/security', label: t('nav.security', 'Sicherheit'), icon: <ShieldCheck className="h-[18px] w-[18px]" /> },
        ]
      : []),
  ];

  // Batch — public documents (GW/TF Checkliste + KYC Formular).
  // Visible to every authenticated CRM user regardless of role.
  const docs: NavLink[] = [
    {
      to: '/dokumente',
      label: t('nav.public_documents', 'Öffentliche Dokumente'),
      icon: <FileText className="h-[18px] w-[18px]" />,
    },
  ];

  const personal: NavLink[] = [
    { to: '/settings', label: t('nav.settings', 'Einstellungen'), icon: <Settings className="h-[18px] w-[18px]" /> },
    { to: '/help', label: t('nav.help', 'Hilfe'), icon: <HelpCircle className="h-[18px] w-[18px]" /> },
  ];

  const isActive = (l: NavLink): boolean => {
    if (l.exact) return path === l.to;
    return path === l.to || path.startsWith(l.to + '/');
  };

  return (
    <UserAppearanceProvider>
    <div className="flex min-h-screen bg-paper">
      {/* ─── Light Editorial Sidebar ─── */}
      <aside
        className="hidden border-e lg:flex lg:w-[256px] lg:flex-col"
        style={{
          background: 'hsl(var(--brand-bg))',
          borderColor: 'hsl(var(--brand-border))',
        }}
      >
        <div className="flex h-full flex-col px-3 py-5">
          {/* Brand */}
          <Link to="/dashboard" className="mb-5 flex flex-col gap-1 px-2">
            <img
              src="/brand/logo-full.png"
              alt="4M Immobilien"
              style={{ height: 36, width: 'auto', objectFit: 'contain', display: 'block' }}
            />
            <span className="text-[10px] font-medium uppercase text-ink-3 tracking-[0.1em]">
              CRM v2.0
            </span>
          </Link>

          <nav className="flex flex-col gap-0.5">
            {main.map((l) => (
              <NavRow key={l.to} link={l} active={isActive(l)} />
            ))}
          </nav>

          <div className="fm-nav-section">{t('nav.section.communication', 'Kommunikation')}</div>
          <nav className="flex flex-col gap-0.5">
            {comm.map((l) => (
              <NavRow key={l.to} link={l} active={isActive(l)} />
            ))}
          </nav>

          <div className="fm-nav-section">{t('nav.section.documents', 'Dokumente')}</div>
          <nav className="flex flex-col gap-0.5">
            {docs.map((l) => (
              <NavRow key={l.to} link={l} active={isActive(l)} />
            ))}
          </nav>

          {admin.length > 0 && (
            <>
              <div className="fm-nav-section">{t('nav.section.admin', 'Admin')}</div>
              <nav className="flex flex-col gap-0.5">
                {admin.map((l) => (
                  <NavRow key={l.to} link={l} active={isActive(l)} />
                ))}
              </nav>
            </>
          )}

          <div className="fm-nav-section">{t('nav.section.personal', 'Persönlich')}</div>
          <nav className="flex flex-col gap-0.5">
            {personal.map((l) => (
              <NavRow key={l.to} link={l} active={isActive(l)} />
            ))}
          </nav>

          <div className="flex-1" />

          {/* Profile menu — click opens a menu (Einstellungen / Passwort /
              Abmelden); logout is behind a confirm, not a stray click. */}
          {profile && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t('auth.account_menu', 'Kontomenü')}
                  className="mt-4 flex w-full items-center gap-2.5 rounded-md border p-2.5 text-start transition-colors hover:bg-[hsl(var(--paper-3))] active:bg-[hsl(var(--paper-2))]"
                  style={{
                    borderColor: 'hsl(var(--brand-border))',
                    background: 'hsl(var(--paper-2))',
                  }}
                >
                  <Avatar name={profile.full_name} src={profile.avatar_url} size="sm" />
                  <div className="min-w-0 flex-1 leading-tight">
                    <div className="truncate text-[13px] font-medium text-ink">
                      {profile.full_name}
                    </div>
                    <div className="mt-0.5 text-[10px] uppercase text-ink-3 tracking-[0.08em]">
                      {profile.role}
                    </div>
                  </div>
                  <MoreHorizontal className="h-4 w-4 text-ink-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-[220px]">
                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <Settings className="h-3.5 w-3.5" />
                    {t('nav.settings', 'Einstellungen')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" hash="security">
                    <KeyRound className="h-3.5 w-3.5" />
                    {t('auth.change_password', 'Passwort ändern')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="danger" onSelect={confirmLogout}>
                  <LogOut className="h-3.5 w-3.5" />
                  {t('auth.logout', 'Abmelden')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </aside>

      {/* ─── Main column ─── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* TopBar */}
        <header data-tour="header" className="flex items-center gap-2 border-b border-line bg-paper px-4 py-3 md:gap-3 md:px-7 md:py-4">
          {/* Mobile + tablet brand (replaces page title — each page has its own h2) */}
          <Link to="/dashboard" className="flex items-center lg:hidden">
            <img
              src="/brand/logo-full.png"
              alt="4M Immobilien"
              style={{ height: 28, width: 'auto', objectFit: 'contain', display: 'block' }}
            />
          </Link>

          {/* Desktop page title */}
          <div className="hidden min-w-0 flex-1 lg:block">
            <h1 className="truncate font-display text-2xl font-normal tracking-tight text-ink">
              {pageTitle(path, t)}
            </h1>
          </div>

          <div className="ms-auto lg:ms-0" />

          {/* Search trigger */}
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="hidden w-72 max-w-[40vw] items-center gap-2 rounded-md bg-paper-2 px-3 py-1.5 text-sm text-ink-3 transition-colors hover:bg-paper-3 lg:flex"
          >
            <Search className="h-4 w-4 opacity-70" />
            <span className="flex-1 truncate text-start">
              {t('app.search.placeholder', 'Klient, Immobilie, Termin…')}
            </span>
            <kbd className="rounded border border-line bg-card px-1.5 py-0.5 font-mono text-[10px]">
              ⌘K
            </kbd>
          </button>

          {/* Theme + lang on all sizes */}
          <ThemeToggle />
          <LanguageSwitcher />

          {/* Chat bell — desktop only. Click → dropdown with unread previews,
              click a row → drawer scrolls to that conversation. Empty list →
              just opens the drawer. */}
          <ChatBell onOpenConversation={() => setChatOpen(true)} />
          <NotificationCenter />
        </header>

        <RealtimeBanner />
        <IdleTimerProvider />

        {/* Page content — keyed by path so the transition replays on navigation */}
        <main className="min-w-0 flex-1 overflow-x-hidden px-4 py-5 pb-24 md:px-8 md:py-7 lg:pb-7">
          <div key={path} className="fm-page-enter">
            <Outlet />
          </div>
        </main>

        {/* Mobile + tablet bottom tabs (4 main + Mehr drawer) — Apple-style glass with
            gold-tinted active pill, top accent bar, and subtle scale lift. */}
        <nav className="sticky bottom-0 z-20 flex border-t border-line bg-paper/85 backdrop-blur-xl backdrop-saturate-150 safe-bottom lg:hidden">
          {main.slice(0, 4).map((l) => {
            const active = isActive(l);
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-semibold transition-all duration-200 ${
                  active ? 'text-ink' : 'text-ink-3 hover:text-ink-2'
                }`}
              >
                {/* Top accent bar — only on active tab */}
                {active && (
                  <span
                    aria-hidden
                    className="absolute left-1/2 top-0 h-[3px] w-9 -translate-x-1/2 rounded-b-full"
                    style={{
                      background:
                        'linear-gradient(90deg, hsl(var(--brand-gold)), hsl(var(--brand-gold-deep)))',
                      boxShadow: '0 0 12px hsl(var(--brand-gold) / 0.4)',
                    }}
                  />
                )}
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-2xl transition-all duration-200 ${
                    active
                      ? 'scale-110 text-[#1a1305] shadow-soft'
                      : 'text-ink-3'
                  }`}
                  style={
                    active
                      ? {
                          background:
                            'linear-gradient(135deg, hsl(var(--brand-gold)), hsl(var(--brand-gold-deep)))',
                        }
                      : undefined
                  }
                >
                  {l.icon}
                </span>
                <span className={`truncate px-1 ${active ? 'mt-0.5' : ''}`}>{l.label}</span>
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className={`relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-semibold transition-all duration-200 ${
              mobileMenuOpen ? 'text-ink' : 'text-ink-3 hover:text-ink-2'
            }`}
          >
            {mobileMenuOpen && (
              <span
                aria-hidden
                className="absolute left-1/2 top-0 h-[3px] w-9 -translate-x-1/2 rounded-b-full"
                style={{
                  background:
                    'linear-gradient(90deg, hsl(var(--brand-gold)), hsl(var(--brand-gold-deep)))',
                  boxShadow: '0 0 12px hsl(var(--brand-gold) / 0.4)',
                }}
              />
            )}
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-2xl transition-all duration-200 ${
                mobileMenuOpen ? 'scale-110 text-[#1a1305] shadow-soft' : 'text-ink-3'
              }`}
              style={
                mobileMenuOpen
                  ? {
                      background:
                        'linear-gradient(135deg, hsl(var(--brand-gold)), hsl(var(--brand-gold-deep)))',
                    }
                  : undefined
              }
            >
              <MoreHorizontal className="h-[18px] w-[18px]" />
            </span>
            <span className={`truncate px-1 ${mobileMenuOpen ? 'mt-0.5' : ''}`}>
              {t('nav.more', 'Mehr')}
            </span>
          </button>
        </nav>

        {/* Mobile + tablet "Mehr" drawer */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
            <button
              type="button"
              aria-label="Close menu"
              className="absolute inset-0 bg-black/40"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div
              className="absolute inset-y-0 end-0 flex w-[min(86vw,340px)] flex-col border-s bg-[hsl(var(--brand-bg))] shadow-strong"
              style={{
                paddingTop: 'env(safe-area-inset-top)',
                borderColor: 'hsl(var(--brand-border))',
              }}
            >
              <div
                className="flex items-center justify-between border-b px-4 py-3"
                style={{ borderColor: 'hsl(var(--brand-border))' }}
              >
                <span className="font-display text-base text-ink">
                  {t('nav.menu', 'Menü')}
                </span>
                <div className="flex items-center gap-1.5">
                  <ThemeToggle />
                  <LanguageSwitcher />
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-label={t('common.close', 'Schließen')}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-md text-ink-2 active:bg-[hsl(var(--paper-2))]"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-3">
                <nav className="flex flex-col gap-0.5">
                  {main.slice(4).map((l) => (
                    <NavRow key={l.to} link={l} active={isActive(l)} />
                  ))}
                </nav>

                <div className="fm-nav-section">
                  {t('nav.section.communication', 'Kommunikation')}
                </div>
                <nav className="flex flex-col gap-0.5">
                  {comm.map((l) => (
                    <NavRow key={l.to} link={l} active={isActive(l)} />
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setChatOpen(true);
                    }}
                    className="fm-nav-item"
                  >
                    <span className="inline-flex">
                      <MessageSquare className="h-[18px] w-[18px]" />
                    </span>
                    <span className="flex-1 text-start">{t('nav.team_chat', 'Team-Chat')}</span>
                    {totalUnread > 0 && (
                      <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[hsl(var(--brand-gold))] px-1 text-[9px] font-bold text-[#1a1305]">
                        {totalUnread > 9 ? '9+' : totalUnread}
                      </span>
                    )}
                  </button>
                </nav>

                <div className="fm-nav-section">
                  {t('nav.section.documents', 'Dokumente')}
                </div>
                <nav className="flex flex-col gap-0.5">
                  {docs.map((l) => (
                    <NavRow key={l.to} link={l} active={isActive(l)} />
                  ))}
                </nav>

                {admin.length > 0 && (
                  <>
                    <div className="fm-nav-section">{t('nav.section.admin', 'Admin')}</div>
                    <nav className="flex flex-col gap-0.5">
                      {admin.map((l) => (
                        <NavRow key={l.to} link={l} active={isActive(l)} />
                      ))}
                    </nav>
                  </>
                )}

                <div className="fm-nav-section">
                  {t('nav.section.personal', 'Persönlich')}
                </div>
                <nav className="flex flex-col gap-0.5">
                  {personal.map((l) => (
                    <NavRow key={l.to} link={l} active={isActive(l)} />
                  ))}
                </nav>
              </div>

              {profile && (
                <div
                  className="border-t px-3 py-3"
                  style={{ borderColor: 'hsl(var(--brand-border))' }}
                >
                  <div className="mb-2 flex items-center gap-2.5 px-2">
                    <Avatar name={profile.full_name} src={profile.avatar_url} size="sm" />
                    <div className="min-w-0 flex-1 leading-tight">
                      <div className="truncate text-[13px] font-medium text-ink">
                        {profile.full_name}
                      </div>
                      <div className="mt-0.5 text-[10px] uppercase text-ink-3 tracking-[0.08em]">
                        {profile.role}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      void confirmLogout();
                    }}
                    className="flex w-full min-h-[44px] items-center gap-2 rounded-md border bg-[hsl(var(--paper-2))] px-3 py-2 text-sm font-medium text-ink active:bg-[hsl(var(--paper-3))]"
                    style={{ borderColor: 'hsl(var(--line))' }}
                  >
                    <LogOut className="h-4 w-4" />
                    {t('auth.logout', 'Abmelden')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <AIChatWidget />
      <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <TeamChatDrawer open={chatOpen} onOpenChange={setChatOpen} />
      <OnboardingTour open={onbOpen} onClose={() => setOnbOpen(false)} />
    </div>
    </UserAppearanceProvider>
  );
}

function NavRow({ link, active }: { link: NavLink; active: boolean }) {
  return (
    <Link to={link.to} className={`fm-nav-item ${active ? 'active' : ''}`}>
      <span className="inline-flex">{link.icon}</span>
      <span className="flex-1">{link.label}</span>
    </Link>
  );
}

function pageTitle(path: string, t: (k: string, fallback: string) => string): string {
  if (path === '/dashboard') return t('nav.dashboard', 'Dashboard');
  if (path.startsWith('/clients')) return t('nav.clients', 'Klienten');
  if (path === '/interessenten') return t('nav.interessenten', 'Interessenten');
  if (path === '/properties') return t('nav.properties', 'Immobilien');
  if (path === '/properties/leads') return t('properties.leads.title', 'Leads & Aufnahme');
  if (path === '/properties/active') return t('properties.active.title', 'Aktive Immobilien');
  if (path === '/appointments') return t('nav.appointments', 'Termine');
  if (path === '/appointments/acquisition') return t('appointments.acquisition.title', 'Aufnahme-Termine');
  if (path === '/appointments/viewing') return t('appointments.viewing.title', 'Besichtigungen');
  if (path === '/offers') return t('nav.offers', 'Kaufanbote');
  if (path.startsWith('/offers/')) return t('offers.detail.title', 'Kaufanbot');
  if (path === '/communication') return t('nav.communication', 'Kommunikation');
  if (path === '/analytics') return t('nav.analytics', 'Analytics');
  if (path === '/admin/transfers') return t('nav.transfers', 'Transfers');
  if (path === '/marketing-admin') return t('nav.madmin', 'Marketing-Admin');
  if (path === '/admin/audit-log') return t('nav.audit', 'Audit-Log');
  if (path === '/admin/zinshaeuser') return t('nav.zinshaeuser', 'Zinshausliste');
  if (path === '/admin/security') return t('nav.security', 'Sicherheit');
  if (path === '/settings') return t('nav.settings', 'Einstellungen');
  if (path === '/help') return t('nav.help', 'Hilfe');
  if (path === '/viewing-confirmations') return t('nav.viewing_conf', 'Besichtigungsschein');
  if (path === '/email-marketing') return t('nav.email_marketing', 'E-Mail-Marketing');
  if (path === '/marketing-uebersicht') return t('nav.muebersicht', 'Marketing-Übersicht');
  if (path === '/marketing') return t('nav.marketing', 'Marketing');
  if (path === '/mediathek') return t('nav.mediathek', 'Mediathek');
  if (path === '/design') return t('nav.design', 'Design');
  if (path === '/kalender') return t('nav.kalender', 'Kalender');
  if (path === '/blog') return t('nav.blog', 'Blog');
  return '4M Immo';
}
